/**
 * Texture Studio (docs/TEXTURE-STUDIO-SPEC.md): edit how a texture set is
 * laid on one surface -- the set, its tile scale, turn, roughness, relief
 * and brightness -- and see that surface's object rendered with it in
 * seconds (a swatch from the back end's Blender, the renderer's own
 * material code), without a scene render. Pin swatches to hold placements
 * against each other; compare with the procedural finish; Apply puts the
 * placement into the photoreal settings (or a Floors by space entry).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Pin, RefreshCw } from 'lucide-react';
import { DesignerModal } from '../chrome/DesignerModal';
import { PLACEMENT_FACTORY, PhotoRenderSettings, TexturePlacement, TextureSurface } from '../../ui/room3d/photoRender';
import { base64Of, itemSwatchGlb, itemsOf } from '../../ui/room3d/itemSwatch';
import type { RenderManifest } from '../../ui/room3d/sceneExport';
import { ServiceDownError, TextureSet, renderSwatch, swatchUrl } from '../../props/studioApi';
import { FloorPreview } from '../../props/FloorPreview';
import { SPACE_TYPES } from '../../../data/presets';
import { MiniSlider, TintRow } from './controls';
import { TexturePicker } from './TexturePicker';
import { DESIGNER_STRINGS as S } from '../strings';

const P = S.photo;
const T = S.textureStudio;

/** The items a surface offers (opening styles): wood styles for the doors' wood, every opening's style for the frames. */
export function itemsFor(surface: TextureSurface): string[] {
  if (surface === 'wood') return itemsOf(['door', 'garageDoor'], true);
  if (surface === 'frame') return itemsOf(['window', 'door', 'garageDoor']);
  return [];
}

/** The surfaces that lie flat (seen on the live preview's floor); the rest stand (seen on its walls). */
const FLAT: TextureSurface[] = ['floor', 'lawn', 'slab'];

/** The item whose real object stands in for a surface's default swatch. */
const SAMPLE_ITEM: Partial<Record<TextureSurface, string>> = { wood: 'door-shaker-walnut', frame: 'window-hung-white' };

export interface TextureStudioProps {
  surface: TextureSurface;
  /** The placement being edited; null starts from the library's first set. */
  initial: TexturePlacement | null;
  /** The item opened on (an opening style id), or '' for the surface's own placement. */
  initialItem?: string;
  /** An item's placement goes here (null: the item back to the surface's). */
  onApplyItem?: (item: string, placement: TexturePlacement | null) => void;
  /** The library's sets, for the picker. */
  sets: TextureSet[];
  /** The photoreal settings the swatch is lit and graded by (and the other surfaces' placements it shows). */
  settings: PhotoRenderSettings;
  /** The space the floor swatch is judged in (its wall base); the picker's start. */
  space?: string;
  /** The placement goes into the settings (null: the surface back to procedural). */
  onApply: (placement: TexturePlacement | null) => void;
  /** Floor only: the placement appended to a space's Floors by space. */
  onSaveToPool?: (placement: TexturePlacement, space: string) => void;
  /** The 3D view's look: the swatch is exposed and lamp-lit by it, as the render is. */
  look?: RenderManifest['look'];
  onClose: () => void;
}

type NumberField = 'scale' | 'rotation' | 'roughness' | 'relief' | 'brightness';
const FIELDS: { key: NumberField; range: 'textures.scale' | 'textures.rotation' | 'textures.roughness' | 'textures.relief' | 'textures.brightness'; step: number; unit?: string }[] = [
  { key: 'scale', range: 'textures.scale', step: 0.05, unit: '×' },
  { key: 'rotation', range: 'textures.rotation', step: 1, unit: '°' },
  { key: 'roughness', range: 'textures.roughness', step: 0.01, unit: '×' },
  { key: 'relief', range: 'textures.relief', step: 0.01, unit: '×' },
  { key: 'brightness', range: 'textures.brightness', step: 0.01, unit: '×' },
];

interface Swatch { url: string | null; busy: boolean; error: string | null; cached?: boolean }
interface Pinned { url: string; placement: TexturePlacement; label: string }

const placementLabel = (p: TexturePlacement) => `${p.id} ×${p.scale} ${p.rotation}° r${p.roughness} b${p.relief} ${p.brightness}${p.tintStrength > 0 ? ` ${p.tint}@${p.tintStrength}` : ''}`;

export const TextureStudio: React.FC<TextureStudioProps> = ({ surface, initial, initialItem = '', onApplyItem, sets, settings, space: space0 = 'garage', onApply, onSaveToPool, look, onClose }) => {
  const items = itemsFor(surface);
  const [item, setItem] = useState<string>(items.includes(initialItem) ? initialItem : '');
  const surfacePlacement = settings.materials.textures[surface];
  const startFor = useCallback((it: string): TexturePlacement | null => (it ? (settings.materials.items[it] ?? surfacePlacement ?? initial) : initial) ?? (sets[0] ? { id: sets[0].id, ...PLACEMENT_FACTORY } : null), [settings, surfacePlacement, initial, sets]);
  const [placement, setPlacement] = useState<TexturePlacement | null>(() => startFor(items.includes(initialItem) ? initialItem : ''));
  const chooseItem = (it: string) => { setItem(it); setPlacement(startFor(it)); };
  // The item's real object, exported once per item (the surface's default shows a sample item's).
  const glbs = useRef(new Map<string, Promise<string | null>>());
  const glbFor = useCallback((it: string): Promise<string | null> => {
    const key = it || SAMPLE_ITEM[surface] || '';
    if (!key) return Promise.resolve(null);
    let p = glbs.current.get(key);
    if (!p) {
      p = itemSwatchGlb(key).then((buf) => (buf ? base64Of(buf) : null)).catch(() => null);
      glbs.current.set(key, p);
    }
    return p;
  }, [surface]);
  const [object, setObject] = useState<'door' | 'garage'>('door');
  const [space, setSpace] = useState(space0);
  // The Blender swatch is extra (a back end with Blender: this PC's helper); the live preview is the studio's own.
  const [blender, setBlender] = useState(false);
  const [auto, setAuto] = useState(true);
  const liveSet = placement ? sets.find((t) => t.id === placement.id) ?? null : null;
  const light = settings.light.world === 'hdri' && settings.light.hdri
    ? { file: settings.light.hdri, strength: settings.light.hdriStrength, rotation: settings.light.hdriRotation }
    : null;
  const [compare, setCompare] = useState(false);
  const [swatch, setSwatch] = useState<Swatch>({ url: null, busy: false, error: null });
  const [plain, setPlain] = useState<Swatch>({ url: null, busy: false, error: null });
  const [pinned, setPinned] = useState<Pinned[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const latest = useRef(0);
  const plainLatest = useRef(0);

  // The settings the swatch is rendered with: the placement on the item (materials.items) or on the surface.
  const settingsWith = useCallback((p: TexturePlacement | null): PhotoRenderSettings => (item
    ? { ...settings, materials: { ...settings.materials, items: p ? { ...settings.materials.items, [item]: p } : Object.fromEntries(Object.entries(settings.materials.items).filter(([k]) => k !== item)) } }
    : { ...settings, materials: { ...settings.materials, textures: { ...settings.materials.textures, [surface]: p } } }), [settings, surface, item]);

  const say = (err: unknown) => (err instanceof ServiceDownError ? P.hdriNoBackEnd : err instanceof Error ? err.message : String(err));

  const render = useCallback(async () => {
    if (!placement) return;
    const n = ++latest.current;
    setSwatch((s) => ({ ...s, busy: true, error: null }));
    try {
      const glb = await glbFor(item);
      const got = await renderSwatch({ surface, object, space, render: settingsWith(placement), ...(look ? { look } : {}), ...(glb ? { item: item || SAMPLE_ITEM[surface], glb } : {}) });
      if (n !== latest.current) return;
      setSwatch({ url: swatchUrl(got.file), busy: false, error: null, cached: got.cached });
    } catch (err) {
      if (n !== latest.current) return;
      setSwatch((s) => ({ ...s, busy: false, error: say(err) }));
    }
  }, [placement, surface, object, space, item, settingsWith, glbFor, look]);

  const renderPlain = useCallback(async () => {
    const n = ++plainLatest.current;
    setPlain((s) => ({ ...s, busy: true, error: null }));
    try {
      const glb = await glbFor(item);
      const got = await renderSwatch({ surface, object, space, procedural: true, render: settingsWith(placement), ...(look ? { look } : {}), ...(glb ? { item: item || SAMPLE_ITEM[surface], glb } : {}) });
      if (n !== plainLatest.current) return;
      setPlain({ url: swatchUrl(got.file), busy: false, error: null });
    } catch (err) {
      if (n !== plainLatest.current) return;
      setPlain((s) => ({ ...s, busy: false, error: say(err) }));
    }
  }, [placement, surface, object, space, item, settingsWith, glbFor, look]);

  // A change re-renders after a pause (auto), the newest only.
  useEffect(() => {
    if (!blender || !auto || !placement) return undefined;
    const t = window.setTimeout(() => { render(); }, 600);
    return () => window.clearTimeout(t);
  }, [blender, auto, placement, object, space, item, render]);
  useEffect(() => {
    if (!blender || !compare) return undefined;
    const t = window.setTimeout(() => { renderPlain(); }, 600);
    return () => window.clearTimeout(t);
  }, [blender, compare, object, space, item, renderPlain]);

  const change = (key: NumberField, v: number) => setPlacement((p) => (p ? { ...p, [key]: v } : p));
  const changeTint = (tint: string, tintStrength: number) => setPlacement((p) => (p ? { ...p, tint, tintStrength } : p));
  const pin = () => {
    if (!swatch.url || !placement) return;
    setPinned((list) => [{ url: swatch.url as string, placement: { ...placement }, label: placementLabel(placement) }, ...list].slice(0, 3));
  };
  const objectsText = (T.objects as Record<string, string>)[surface] ?? '';
  const title = T.title(item ? ((S.openingStyleName as Record<string, string>)[item] ?? item) : ((P as unknown as Record<string, string>)[`texture${surface[0].toUpperCase()}${surface.slice(1)}`] ?? surface));

  return (
    <DesignerModal title={title} onClose={onClose} width={920} icon={<Camera size={16} color="#FEB100" />}>
      <div className="ts-studio">
        <div className="ts-left">
          <p className="rc-note" style={{ marginTop: 0 }}>{objectsText}</p>
          {items.length > 0 && (
            <div className="rc-row">
              <div className="rc-row-head">
                <span className="rc-label" title={T.itemHint}>{T.item}</span>
                <select className="rc-select" aria-label={T.item} value={item} onChange={(e) => chooseItem(e.target.value)}>
                  <option value="">{T.itemAll}</option>
                  {items.map((id) => <option key={id} value={id}>{(S.openingStyleName as Record<string, string>)[id] ?? id}{settings.materials.items[id] ? ' ●' : ''}</option>)}
                </select>
              </div>
              {item && <p className="rc-note" style={{ marginTop: 2 }}>{settings.materials.items[item] ? T.itemOwn : T.itemFromSurface}</p>}
            </div>
          )}
          <div className="rc-row">
            <div className="rc-row-head">
              <span className="rc-label">{T.set}</span>
              <TexturePicker
                sets={sets} value={placement?.id ?? ''} label={T.set} noneLabel={placement ? undefined : T.noSet}
                onChange={(id) => setPlacement((p) => (id ? { ...PLACEMENT_FACTORY, ...(p ?? {}), id } : null))}
              />
            </div>
          </div>
          {placement && FIELDS.map(({ key, range, step, unit }) => (
            <MiniSlider
              key={key} label={(P.finishField as Record<string, string>)[key] ?? key} hint={(T.fieldHints as Record<string, string>)[key]} value={placement[key]} range={range} step={step} unit={unit}
              onChange={(v) => change(key, v)} onReset={placement[key] !== PLACEMENT_FACTORY[key] ? () => change(key, PLACEMENT_FACTORY[key]) : undefined}
            />
          ))}
          {placement && (
            <TintRow
              tint={placement.tint} strength={placement.tintStrength} onChange={changeTint}
              labels={{ tint: P.finishField.tint, strength: P.finishField.tintStrength }} hints={{ tint: T.fieldHints.tint, strength: T.fieldHints.tintStrength }}
            />
          )}
          {surface === 'wood' && !item && (
            <div className="rc-row">
              <div className="rc-row-head">
                <span className="rc-label">{T.object}</span>
                <select className="rc-select" aria-label={T.object} value={object} onChange={(e) => setObject(e.target.value as 'door' | 'garage')}>
                  <option value="door">{T.objectDoor}</option><option value="garage">{T.objectGarage}</option>
                </select>
              </div>
            </div>
          )}
          {surface === 'floor' && onSaveToPool && (
            <div className="rc-row">
              <div className="rc-row-head">
                <span className="rc-label" title={T.spaceHint}>{P.finishSpace}</span>
                <select className="rc-select" aria-label={P.finishSpace} value={space} onChange={(e) => setSpace(e.target.value)}>
                  {SPACE_TYPES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
            </div>
          )}
          {blender && (
            <>
              <div className="rc-row">
                <div className="rc-row-head">
                  <span className="rc-label" title={T.autoHint}>{T.auto}</span>
                  <input className="rc-switch" type="checkbox" aria-label={T.auto} checked={auto} onChange={(e) => setAuto(e.target.checked)} />
                </div>
              </div>
              <div className="rc-row">
                <div className="rc-row-head">
                  <span className="rc-label" title={T.compareHint}>{T.compare}</span>
                  <input className="rc-switch" type="checkbox" aria-label={T.compare} checked={compare} onChange={(e) => setCompare(e.target.checked)} />
                </div>
              </div>
            </>
          )}
          <div className="rc-actions" style={{ marginTop: 10 }}>
            <button type="button" className="rc-action is-active" disabled={!placement} title={item ? T.applyItemHint : T.applyHint} onClick={() => { if (item && onApplyItem) onApplyItem(item, placement); else onApply(placement); onClose(); }}>{item ? T.applyItem : T.apply}</button>
            {onSaveToPool && <button type="button" className="rc-action" disabled={!placement} title={T.saveToPoolHint} onClick={() => { if (placement) { onSaveToPool(placement, space); setNote(T.savedToPool(space)); } }}>{T.saveToPool}</button>}
            <button type="button" className="rc-action" title={item ? T.removeItemHint : T.removeHint} onClick={() => { if (item && onApplyItem) onApplyItem(item, null); else onApply(null); onClose(); }}>{item ? T.removeItem : T.remove}</button>
            <button type="button" className="rc-action" onClick={onClose}>{S.stageDialog.close}</button>
          </div>
          {note && <p className="rc-note">{note}</p>}
        </div>
        <div className="ts-right">
          {/* Live, in the browser: the set laid as the placement says, lit by the look's HDRI. */}
          {liveSet && placement
            ? <FloorPreview key={surface} set={liveSet} placement={placement} light={light} on={FLAT.includes(surface) ? 'floor' : 'walls'} />
            : <div className="ts-swatch-empty">{T.noSet}</div>}
          <p className="rc-note">{T.liveNote}</p>
          <button type="button" className="rc-action" title={T.blenderHint} onClick={() => setBlender((b) => !b)} style={{ marginBottom: 8 }}>
            {blender ? T.blenderHide : T.blenderShow}
          </button>
          {blender && (
          <>
          <div className="ts-swatch-row">
            <div className="ts-swatch">
              {swatch.url ? <img src={swatch.url} alt={T.swatchAlt} /> : <div className="ts-swatch-empty">{placement ? (swatch.busy ? T.rendering : T.noSwatchYet) : T.noSet}</div>}
              {swatch.busy && swatch.url && <div className="ts-swatch-veil">{T.rendering}</div>}
              <div className="ts-swatch-bar">
                <span>{T.current}{swatch.cached ? ` ${T.cached}` : ''}</span>
                <button type="button" className="rc-icon-btn" title={T.renderNow} aria-label={T.renderNow} disabled={!placement || swatch.busy} onClick={render}><RefreshCw size={13} /></button>
                <button type="button" className="rc-icon-btn" title={T.pin} aria-label={T.pin} disabled={!swatch.url || swatch.busy} onClick={pin}><Pin size={13} /></button>
              </div>
            </div>
            {compare && (
              <div className="ts-swatch">
                {plain.url ? <img src={plain.url} alt={T.plainAlt} /> : <div className="ts-swatch-empty">{plain.busy ? T.rendering : T.noSwatchYet}</div>}
                {plain.busy && plain.url && <div className="ts-swatch-veil">{T.rendering}</div>}
                <div className="ts-swatch-bar"><span>{T.procedural}</span></div>
              </div>
            )}
          </div>
          {swatch.error && <p className="rc-note is-bad">{swatch.error}</p>}
          {plain.error && compare && <p className="rc-note is-bad">{plain.error}</p>}
          {pinned.length > 0 && (
            <div className="ts-pins">
              {pinned.map((p, i) => (
                <button key={`${p.url}-${i}`} type="button" className="ts-pin" title={`${T.pinnedHint}\n${p.label}`} onClick={() => setPlacement({ ...p.placement })}>
                  <img src={p.url} alt={p.label} />
                  <span>{p.label}</span>
                </button>
              ))}
            </div>
          )}
          <p className="rc-note">{T.note}</p>
          </>
          )}
        </div>
      </div>
    </DesignerModal>
  );
};

export default TextureStudio;
